import { InfoCircleOutlined } from '@ant-design/icons';

import {
  buildCommonValidationMessages,
  emailField,
  optionalArrayField,
  optionalString,
  phoneField,
  requiredString,
  withMaxLength,
} from '@/utils/validation/schemaHelpers';

import type { FieldDef } from './types';
import type { DefaultOptionType } from 'antd/es/select';
import type { TFunction } from 'i18next';

type PersonalInfoRecruiterFieldsOptions = {
  t: TFunction;
  showCompany?: boolean;
  isOnboarding?: boolean;
  hasCompany?: boolean;
  companyLocked?: boolean;
  specializationOptions?: DefaultOptionType[];
  includeProfileFields?: boolean;
  includeCity?: boolean;
  includeSpecializations?: boolean;
};

export function buildPersonalInfoRecruiterFieldDefs(
  options: PersonalInfoRecruiterFieldsOptions,
): FieldDef[] {
  const {
    t,
    showCompany = true,
    isOnboarding = false,
    hasCompany = false,
    companyLocked = false,
    specializationOptions = [],
    includeProfileFields = false,
    includeCity = true,
    includeSpecializations = false,
  } = options;
  const messages = buildCommonValidationMessages(t);

  const fields: FieldDef[] = [
    {
      name: 'name',
      validation: withMaxLength(
        requiredString(messages.required),
        128,
        messages.maxLength(128),
      ),
      ui: {
        type: 'text',
        label: t('NAME_LABEL'),
      },
    },
    {
      name: 'title',
      validation: withMaxLength(
        requiredString(messages.required),
        255,
        messages.maxLength(255),
      ),
      ui: {
        type: 'text',
        label: t('TITLE_TEXT'),
        colSpan: 12,
      },
    },
  ];

  if (showCompany) {
    fields.push({
      name: 'company',
      validation: optionalString(),
      ui: {
        type: 'text',
        label: t('COMPANY_TITLE'),
        colSpan: 12,
        componentProps: {
          disabled: !isOnboarding || hasCompany || companyLocked,
          placeholder:
            isOnboarding && !hasCompany && !companyLocked
              ? t('ENTER_COMPANY_NAME')
              : undefined,
        },
      },
    });
  }

  if (includeCity) {
    fields.push({
      name: 'city',
      validation: requiredString(messages.required),
      ui: {
        type: 'places',
        label: t('LOCATION_CITY_LABEL'),
        placeholder: t('LOCATION_CITY_PLACEHOLDER'),
      },
    });
  }

  fields.push(
    {
      name: 'email',
      validation: emailField(messages),
      ui: {
        type: 'text',
        label: t('EMAIL'),
        colSpan: 12,
        componentProps: { disabled: true, type: 'email' },
      },
    },
    {
      name: 'phone',
      validation: phoneField(messages, false),
      ui: {
        type: 'text',
        label: t('PHONE'),
        colSpan: 12,
      },
    },
  );

  if (includeProfileFields) {
    fields.push(
      {
        name: 'description',
        validation: optionalString(),
        ui: {
          type: 'textarea',
          label: t('ABOUT_YOURSELF'),
          componentProps: { autoSize: { minRows: 3 } },
        },
      },
      {
        name: 'about_company',
        validation: optionalString(),
        ui: {
          type: 'textarea',
          label: t('ABOUT_YOUR_COMPANY'),
          componentProps: { autoSize: { minRows: 3 } },
        },
      },
    );
  }

  if (includeSpecializations) {
    fields.push({
      name: 'preferable_specializations',
      validation: optionalArrayField(),
      ui: {
        type: 'select',
        label: t('YOUR_SPECIALIZATIONS'),
        tooltip: t('JOB_INFO_FORM_SPECIALIZATION_TOOLTIP')
          ? {
              title: t('JOB_INFO_FORM_SPECIALIZATION_TOOLTIP'),
              icon: <InfoCircleOutlined />,
            }
          : false,
        componentProps: {
          mode: 'multiple',
          options: specializationOptions,
        },
      },
    });
  }

  return fields;
}
